import React, { createContext, useReducer, useContext, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import api from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const GUEST_WISHLIST_KEY = 'guestWishlist';

function getGuestWishlistProductIds() {
  try {
    const raw = localStorage.getItem(GUEST_WISHLIST_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const items = Array.isArray(parsed) ? parsed : [];
    return items
      .map((it) => (it?.productId ?? it?._id ?? it?.id))
      .map((id) => (id == null ? '' : String(id).trim()))
      .filter((id) => id.length === 24);
  } catch {
    return [];
  }
}

const AuthStateContext = createContext(null);
const AuthDispatchContext = createContext(null);

export const useAuthState = () => {
  const context = useContext(AuthStateContext);
  if (!context) {
    throw new Error('useAuthState must be used within AuthProvider');
  }
  return context;
};

export const useAuthDispatch = () => {
  const context = useContext(AuthDispatchContext);
  if (!context) {
    throw new Error('useAuthDispatch must be used within AuthProvider');
  }
  return context;
};

// Backwards-compatible hook for existing components.
export const useAuth = () => {
  const state = useAuthState();
  const actions = useAuthDispatch();
  return { ...state, ...actions };
};

const initialState = {
  user: null,
  loading: true,
  hasToken: false,
};

function authReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: Boolean(action.loading) };
    case 'SET_HAS_TOKEN':
      return { ...state, hasToken: Boolean(action.hasToken) };
    case 'SET_USER':
      return { ...state, user: action.user ?? null };
    case 'RESET_AUTH':
      return { ...state, user: null, hasToken: false };
    default:
      return state;
  }
}

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('accessToken');
      const storedUser = localStorage.getItem('user');
      dispatch({ type: 'SET_HAS_TOKEN', hasToken: Boolean(token) });

      if (token && storedUser) {
        try {
          // Optimistically restore user from localStorage first (prevents logout on hard refresh)
          const parsedUser = JSON.parse(storedUser);
          dispatch({ type: 'SET_USER', user: parsedUser });

          // Then verify with API call in the background
          try {
            const response = await api.get('/auth/me');
            // Update user with fresh data from server (including role)
            dispatch({ type: 'SET_USER', user: response.data.user });
            // Update localStorage with fresh user data
            localStorage.setItem('user', JSON.stringify(response.data.user));
          } catch (apiError) {
            // If API call fails, check if it's a network error or auth error
            if (apiError.response?.status === 401 || apiError.response?.status === 403) {
              // Try to refresh token first
              const refreshToken = localStorage.getItem('refreshToken');
              if (refreshToken) {
                try {
                  // Use axios directly to avoid circular dependency
                  const refreshResponse = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
                  const { accessToken } = refreshResponse.data;
                  localStorage.setItem('accessToken', accessToken);
                  // Retry the /auth/me call
                  const retryResponse = await api.get('/auth/me');
                  dispatch({ type: 'SET_USER', user: retryResponse.data.user });
                  localStorage.setItem('user', JSON.stringify(retryResponse.data.user));
                  return; // Success, exit early
                } catch (refreshError) {
                  // Refresh failed, clear everything
                  localStorage.removeItem('accessToken');
                  localStorage.removeItem('refreshToken');
                  localStorage.removeItem('user');
                  dispatch({ type: 'RESET_AUTH' });
                }
              } else {
                // No refresh token, clear everything
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('user');
                dispatch({ type: 'RESET_AUTH' });
              }
            }
            // For network errors, keep the user logged in with cached data
            // The user will be logged out on next API call that requires auth
          }
        } catch (parseError) {
          // If stored user data is corrupted, clear everything
          // Error handled by clearing invalid data
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          dispatch({ type: 'RESET_AUTH' });
        }
      } else {
        // No token or user stored, ensure clean state
        dispatch({ type: 'RESET_AUTH' });
      }
      dispatch({ type: 'SET_LOADING', loading: false });
    };

    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const guestWishlistProductIds = getGuestWishlistProductIds();
      const response = await api.post('/auth/login', {
        email,
        password,
        ...(guestWishlistProductIds.length ? { guestWishlistProductIds } : {}),
      });
      const { user: userData, accessToken, refreshToken } = response.data;

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.removeItem(GUEST_WISHLIST_KEY);

      dispatch({ type: 'SET_USER', user: userData });
      dispatch({ type: 'SET_HAS_TOKEN', hasToken: true });
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed',
      };
    }
  }, []);

  const signup = useCallback(async (name, email, password) => {
    try {
      const guestWishlistProductIds = getGuestWishlistProductIds();
      const response = await api.post('/auth/signup', {
        name,
        email,
        password,
        ...(guestWishlistProductIds.length ? { guestWishlistProductIds } : {}),
      });
      const { user: userData, accessToken, refreshToken } = response.data;

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.removeItem(GUEST_WISHLIST_KEY);

      dispatch({ type: 'SET_USER', user: userData });
      dispatch({ type: 'SET_HAS_TOKEN', hasToken: true });
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Signup failed',
        errors: error.response?.data?.errors,
      };
    }
  }, []);

  const googleAuth = useCallback(async (googleToken) => {
    try {
      const guestWishlistProductIds = getGuestWishlistProductIds();
      const response = await api.post('/auth/google', {
        accessToken: googleToken,
        ...(guestWishlistProductIds.length ? { guestWishlistProductIds } : {}),
      });
      const { user: userData, accessToken, refreshToken } = response.data;

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.removeItem(GUEST_WISHLIST_KEY);

      dispatch({ type: 'SET_USER', user: userData });
      dispatch({ type: 'SET_HAS_TOKEN', hasToken: true });
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Google authentication failed',
      };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Error handled by clearing tokens regardless
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      dispatch({ type: 'RESET_AUTH' });
    }
  }, []);

  const forgotPassword = useCallback(async (email) => {
    try {
      await api.post('/auth/forgot-password', { email });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send reset email',
      };
    }
  }, []);

  const resetPassword = useCallback(async (token, password) => {
    try {
      await api.post('/auth/reset-password', { token, password });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Password reset failed',
        errors: error.response?.data?.errors,
      };
    }
  }, []);

  const updateUser = useCallback((updatedUser) => {
    dispatch({ type: 'SET_USER', user: updatedUser });
    localStorage.setItem('user', JSON.stringify(updatedUser));
  }, []);

  const memoState = useMemo(() => {
    const user = state.user;
    const hasToken = state.hasToken;
    return {
      user,
      loading: state.loading,
      // Authenticated UI + protected calls require BOTH a user and a real token.
      // This prevents a cached user from causing 401s during token expiry or logout races.
      isAuthenticated: Boolean(user && hasToken),
    };
  }, [state.user, state.loading, state.hasToken]);

  const actions = useMemo(
    () => ({
      login,
      signup,
      googleAuth,
      logout,
      forgotPassword,
      resetPassword,
      updateUser,
    }),
    [login, signup, googleAuth, logout, forgotPassword, resetPassword, updateUser]
  );

  return (
    <AuthStateContext.Provider value={memoState}>
      <AuthDispatchContext.Provider value={actions}>
        {children}
      </AuthDispatchContext.Provider>
    </AuthStateContext.Provider>
  );
};
